import React, { useState, useEffect, useMemo } from "react";
import type { IPaymentDetailsStepProps, IPaymentFormData } from "./definitions";
import {
  Alert,
  Box,
  Checkbox,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react/polaris";
import {
  ControlledSelect,
  ControlledTextarea,
  ControlledDatePicker,
  ControlledInput,
} from "@amzn/react-hook-form-polaris";
import { get, useFormState } from "react-hook-form";
import MonetaryInput from "components/monetary-input/MonetaryInput";
import { useGetScanResultQuery } from "pages/draft-overview/endpoints";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import { useListTaxAreaByTeamQuery } from "pages/team_settings/endpoints";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { GaiaConstants } from "utils";
import {
  useBreakdownState,
  calculateAssessmentTotal,
  makeUpdateAmount,
  calculateBreakdownTotals,
} from "components/assessment-details-step/utils";
import { useLocalStorage } from "hooks/useLocalStorage";
import OutcomeBreakdownRow from "components/outcome-breakdown-row";
import { TaxYearAreaBreakdown, OutcomeBreakdownType } from "models/schemas";
import { formatDateValue } from "utils/date-format";
import styles from "./style.module.scss";

/**
 * Parse payment breakdowns from the germany_rc scan result.
 * Each entry in `breakdowns` becomes a TaxYearAreaBreakdown row,
 * preserving the individual dueDate per installment.
 * Amounts are summed to populate the payment overview totals.
 */
function parsePaymentBreakdownsFromScanResult(
  scanResult: any,
): TaxYearAreaBreakdown[] {
  if (!scanResult.breakdowns) return [];
  try {
    const breakdownsArray: any[] = JSON.parse(scanResult.breakdowns);
    return breakdownsArray.map((item, index) => ({
      outcomeBreakdownId: `temp-payment-breakdown-${index}`,
      parentId: "",
      type: OutcomeBreakdownType.TAXYEARAREA as "Tax year & area",
      category: `${item.period || ""}`,
      taxYear: item.period || "",
      taxAreaId: "",
      totalTaxAmount: item.tax || "0",
      totalPenaltyAmount: item.penalty || "0",
      totalInterestAmount: item.interest || "0",
      totalOtherAmount: item.other || "0",
      dueDate: item.dueDate || "",
      additionalInformation: "",
    }));
  } catch (error) {
    console.error(
      "Failed to parse payment breakdowns from scan result:",
      error,
    );
    return [];
  }
}

const PaymentDetailsStep: React.FC<IPaymentDetailsStepProps> = ({
  shortIdForScanJob,
  currencySign,
  isScanWizard = false,
  externalFormData,
  onFormDataChange,
  taxAreaOptions = [],
  gaiaObjectId,
  isCreateMode,
  category,
  control,
  setValue,
  aboveTaxYearAreaBreakdown,
  onBreakdownsChange,
}) => {
  const formState = useFormState({ control });
  const {
    data: scanResultData,
    isLoading,
    error,
  } = useGetScanResultQuery(
    { shortId: shortIdForScanJob! },
    { skip: !isScanWizard || !shortIdForScanJob },
  );
  const uiContent: any = useGaiaObjectUiContent(category);
  const storageKey = `payment-step-${shortIdForScanJob}`;

  const [internalFormData, setInternalFormData] =
    useLocalStorage<IPaymentFormData>(storageKey, {
      paymentDate: "",
      type: isScanWizard ? "Payment" : "",
      notes: "",
      paymentRefNumber: "",
      trackingRefNumber: "",
      paymentAuthorizationLink: "",
      amounts: {
        totalTaxAmount: "0",
        totalPenaltyAmount: "0",
        totalInterestAmount: "0",
        totalOtherAmount: "0",
      },
      breakdowns: {
        byTaxYear: false,
        byTaxArea: false,
        byTaxYearArea: false,
      },
    });

  const formData =
    !isScanWizard && externalFormData ? externalFormData : internalFormData;
  const setFormData =
    !isScanWizard && onFormDataChange ? onFormDataChange : setInternalFormData;

  const [parsedTaxYearAreaBreakdowns, setParsedTaxYearAreaBreakdowns] =
    useState<TaxYearAreaBreakdown[]>([]);
  const { teamName } = useTeamNameSelector();
  const { data: listTaxAreaData } = useListTaxAreaByTeamQuery(
    { teamName },
    {
      refetchOnMountOrArgChange: true,
      skip: !teamName || teamName === GaiaConstants.PLACE_HOLDER,
    },
  );
  const teamTaxAreaOptions = useMemo(
    () =>
      listTaxAreaData?.taxAreas?.taxAreas
        ?.filter((ta) => ta.status === "Active")
        ?.map((ta) => ({ value: ta.taxAreaId, label: ta.title })) || [],
    [listTaxAreaData],
  );

  const {
    state: breakdownState,
    handlers: breakdownHandlers,
    disabledStates,
  } = useBreakdownState(taxAreaOptions.length > 0);

  useEffect(() => {
    if (formData.breakdowns) {
      const hasChanged =
        breakdownState.byTaxYear !== formData.breakdowns.byTaxYear ||
        breakdownState.byTaxArea !== formData.breakdowns.byTaxArea ||
        breakdownState.byTaxYearArea !== formData.breakdowns.byTaxYearArea;

      if (hasChanged) {
        if (formData.breakdowns.byTaxYear)
          breakdownHandlers.handleTaxYearChange(true);
        if (formData.breakdowns.byTaxArea)
          breakdownHandlers.handleTaxAreaChange(true);
        if (formData.breakdowns.byTaxYearArea)
          breakdownHandlers.handleTaxYearAreaChange(true);
      }
    }
  }, [
    formData.breakdowns?.byTaxYear,
    formData.breakdowns?.byTaxArea,
    formData.breakdowns?.byTaxYearArea,
  ]);

  useEffect(() => {
    if (
      formData.breakdowns?.byTaxYear !== breakdownState.byTaxYear ||
      formData.breakdowns?.byTaxArea !== breakdownState.byTaxArea ||
      formData.breakdowns?.byTaxYearArea !== breakdownState.byTaxYearArea
    ) {
      setFormData({
        ...formData,
        breakdowns: breakdownState,
      });
    }
  }, [breakdownState, formData.breakdowns]);

  const updateAmount = makeUpdateAmount(
    formData as any,
    setFormData as any,
    control,
  );
  const paymentTypeOptions = uiContent?.paymentTypeOptions || [];

  useEffect(() => {
    if (!scanResultData?.scanResult || !isScanWizard) return;

    const result = scanResultData.scanResult as any;

    // Parse payment breakdowns — each row has its own dueDate
    const rawBreakdowns = parsePaymentBreakdownsFromScanResult(result);

    // Match taxArea from scan result against team tax area options and pre-select it on each row
    const matchedTaxArea = teamTaxAreaOptions.find(
      (ta) =>
        ta.label.toLowerCase() === (result.taxArea || "").toLowerCase() ||
        ta.value.toLowerCase() === (result.taxArea || "").toLowerCase(),
    );
    const breakdowns = rawBreakdowns.map((b) => ({
      ...b,
      taxAreaId: matchedTaxArea ? matchedTaxArea.value : b.taxAreaId,
      category: `${b.taxYear || ""}${
        matchedTaxArea ? matchedTaxArea.value : ""
      }`,
    }));
    setParsedTaxYearAreaBreakdowns(breakdowns);

    // Sum amounts from breakdowns (germany_rc has no top-level total_tax_amount)
    const { totalTax, totalPenalty, totalInterest, totalOther } =
      calculateBreakdownTotals(breakdowns);

    // Use noticeDate as paymentDate (the most relevant date from the scan)
    const paymentDate = result.noticeDate
      ? formatDateValue(Number.parseInt(result.noticeDate, 10))
      : result.dateReceived
        ? formatDateValue(Number.parseInt(result.dateReceived, 10))
        : "";

    setFormData({
      ...formData,
      paymentDate,
      type: result.paymentType || "Payment",
      notes: result.notes || "",
      parsedTaxYearAreaBreakdowns: breakdowns,
      amounts: {
        totalTaxAmount:
          result.total_tax_amount || (totalTax > 0 ? String(totalTax) : "0"),
        totalPenaltyAmount:
          result.total_penalty_amount ||
          (totalPenalty > 0 ? String(totalPenalty) : "0"),
        totalInterestAmount:
          result.total_interest_amount ||
          (totalInterest > 0 ? String(totalInterest) : "0"),
        totalOtherAmount:
          result.total_other_amount ||
          (totalOther > 0 ? String(totalOther) : "0"),
      },
    });
  }, [scanResultData, isScanWizard, teamTaxAreaOptions]);

  // Notify parent whenever parsed breakdowns change (used for superseding notice display)
  useEffect(() => {
    if (onBreakdownsChange) {
      onBreakdownsChange(parsedTaxYearAreaBreakdowns);
    }
  }, [parsedTaxYearAreaBreakdowns, onBreakdownsChange]);

  // Sync formData with react-hook-form to display values in UI
  useEffect(() => {
    if (!setValue) return;
    if (formData.paymentDate)
      setValue("paymentDate" as any, formData.paymentDate);
    if (formData.type) setValue("type" as any, formData.type);
    if (formData.notes) setValue("notes" as any, formData.notes);
  }, [formData.paymentDate, formData.type, formData.notes, setValue]);

  const total = useMemo(() => {
    return calculateAssessmentTotal({
      tax: formData.amounts.totalTaxAmount,
      penalty: formData.amounts.totalPenaltyAmount,
      interest: formData.amounts.totalInterestAmount,
      other: formData.amounts.totalOtherAmount,
    });
  }, [formData.amounts]);

  if (isLoading) {
    return (
      <Container>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <div className={styles.loadingText}>Loading payment data...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert type="error" header="Error loading payment data">
          Unable to load scan result data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container>
        <SpaceBetween direction="vertical" size="m">
          {/* Payment date + type */}
          <div className={styles.firstRowWrapper}>
            <div className={styles.dateCustomPicker}>
              <FormField
                label="Payment date"
                errorText={get(formState.errors, "paymentDate.message")}
              >
                <ControlledDatePicker
                  name="paymentDate"
                  control={control}
                  placeholder="YYYY/MM/DD"
                  onChange={({ detail }) => {
                    setFormData({ ...formData, paymentDate: detail.value });
                  }}
                />
              </FormField>
            </div>
            <div className={styles.typeBox}>
              <FormField
                label="Type"
                errorText={get(formState.errors, "type.message")}
              >
                <ControlledSelect
                  name="type"
                  options={paymentTypeOptions}
                  control={control}
                  placeholder="Select payment type"
                  onChange={({ detail }) => {
                    setFormData({
                      ...formData,
                      type: detail.selectedOption.value || "",
                    });
                  }}
                />
              </FormField>
            </div>
          </div>

          {/* Reference fields */}
          <div className={styles.secondRowWrapper}>
            <FormField
              label="Payment reference number"
              errorText={get(formState.errors, "paymentRefNumber.message")}
            >
              <ControlledInput
                name="paymentRefNumber"
                control={control}
                onChange={({ detail }) => {
                  setFormData({ ...formData, paymentRefNumber: detail.value });
                }}
              />
            </FormField>
            <FormField
              label="Tracking reference number"
              errorText={get(formState.errors, "trackingRefNumber.message")}
            >
              <ControlledInput
                name="trackingRefNumber"
                control={control}
                onChange={({ detail }) => {
                  setFormData({
                    ...formData,
                    trackingRefNumber: detail.value,
                  });
                }}
              />
            </FormField>
          </div>
          <div className={styles.secondRowWrapper}>
            <FormField
              label="Payment authorization link"
              errorText={get(
                formState.errors,
                "paymentAuthorizationLink.message",
              )}
            >
              <ControlledInput
                name="paymentAuthorizationLink"
                control={control}
                onChange={({ detail }) => {
                  setFormData({
                    ...formData,
                    paymentAuthorizationLink: detail.value,
                  });
                }}
              />
            </FormField>
          </div>

          {/* Notes */}
          <div className={styles.notesBox}>
            <FormField
              label="Notes"
              errorText={get(formState.errors, "notes.message")}
            >
              <ControlledTextarea
                name="notes"
                control={control}
                placeholder=""
                rows={3}
                onChange={({ detail }) => {
                  setFormData({ ...formData, notes: detail.value });
                }}
              />
            </FormField>
          </div>
        </SpaceBetween>
      </Container>

      {/* Payment amounts overview */}
      <Container
        header={
          <div className={styles.headerContainer}>
            <Header variant="h2">Payment overview</Header>
            <i>Note: refunds should be entered as negative values</i>
          </div>
        }
      >
        <div className={styles.overviewWrapper}>
          <FormField
            label="Tax"
            controlId="totalTaxAmount"
            errorText={get(formState.errors, "totalTaxAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalTaxAmount}
              onChange={(value) => updateAmount("totalTaxAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Penalty"
            controlId="totalPenaltyAmount"
            errorText={get(formState.errors, "totalPenaltyAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalPenaltyAmount}
              onChange={(value) => updateAmount("totalPenaltyAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Interest"
            controlId="totalInterestAmount"
            errorText={get(formState.errors, "totalInterestAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalInterestAmount}
              onChange={(value) => updateAmount("totalInterestAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Other"
            controlId="totalOtherAmount"
            errorText={get(formState.errors, "totalOtherAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalOtherAmount}
              onChange={(value) => updateAmount("totalOtherAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <div className={styles.dollarInput}>
            <FormField label="Total">
              <div className={styles.inputContainer}>
                <span className={styles.dollarSign}> {currencySign} </span>
                <input
                  value={total}
                  readOnly={true}
                  className={styles.inputField}
                />
              </div>
            </FormField>
          </div>
        </div>

        {/* Breakdown checkboxes — only shown outside scan wizard */}
        {!isScanWizard && (
          <>
            <br />
            <Box>
              <SpaceBetween direction="vertical" size="s">
                <div>
                  <Header variant="h3">Further breakdown the amount by</Header>
                </div>
                <div>
                  <SpaceBetween direction="vertical" size="xs">
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearChange(detail.checked)
                      }
                      checked={breakdownState.byTaxYear}
                      disabled={disabledStates.taxYear}
                    >
                      Tax year
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxAreaChange(detail.checked)
                      }
                      checked={breakdownState.byTaxArea}
                      disabled={disabledStates.taxArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax area
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearAreaChange(
                          detail.checked,
                        )
                      }
                      checked={breakdownState.byTaxYearArea}
                      disabled={disabledStates.taxYearArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax year & area
                    </Checkbox>
                  </SpaceBetween>
                </div>
              </SpaceBetween>
            </Box>
          </>
        )}
      </Container>
      {aboveTaxYearAreaBreakdown}

      {/* Scan wizard: payment by tax year & area (with per-row dueDate) */}
      {parsedTaxYearAreaBreakdowns.length > 0 && (
        <Container
          header={<Header variant="h2">Payment by tax year & area</Header>}
        >
          <SpaceBetween direction="vertical" size="xl">
            {parsedTaxYearAreaBreakdowns.map((breakdown, index) => (
              <OutcomeBreakdownRow
                key={`${breakdown.outcomeBreakdownId || index}-${
                  breakdown.taxAreaId
                }`}
                breakdown={breakdown}
                currencySign={currencySign}
                isFirstRow={index === 0}
                taxAreaOptions={teamTaxAreaOptions}
                setBreakdownInList={(updatedBreakdown) => {
                  const updated = parsedTaxYearAreaBreakdowns.map((b) =>
                    b.outcomeBreakdownId === updatedBreakdown.outcomeBreakdownId
                      ? (updatedBreakdown as TaxYearAreaBreakdown)
                      : b,
                  );
                  setParsedTaxYearAreaBreakdowns(updated);
                  const { totalTax, totalPenalty, totalInterest, totalOther } =
                    calculateBreakdownTotals(updated);
                  setFormData({
                    ...formData,
                    parsedTaxYearAreaBreakdowns: updated,
                    amounts: {
                      totalTaxAmount: String(totalTax),
                      totalPenaltyAmount: String(totalPenalty),
                      totalInterestAmount: String(totalInterest),
                      totalOtherAmount: String(totalOther),
                    },
                  });
                }}
                onRemove={() => {}}
                showRemoveButton={false}
              />
            ))}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default PaymentDetailsStep;
